<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { postReq } from 'src/services/http';
	//Helpers
	import { devices_settings } from 'src/stores';
	import { onMount } from 'svelte';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Input, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		fetchWaveConfigurationParam,
		mergeWaveConfigurationParam,
		parseConfigurationGetValue,
	} from 'src/helpers/z_wave_configuration_helpers';
	import { zwaveCall } from 'src/services/xz_http_reqs';
	import { format } from '../../../../services/i18n';

	let id = real_id(device.id);
	let settings = readDeviceSettings(id)['Configuration:0'];
	const setting = 85;
	const paramKey = String(setting);
	const min = 3;
	const max = 50;

	let idleTime: number | string = '';
	let cacheIdle: number | string = '';
	let loading = false;
	let fetching = false;
	let lastSynced: number | undefined;

	$: settings = $devices_settings?.[real_id(id)];
	$: storedValue = settings?.['Configuration:0']?.[paramKey];
	$: if (typeof storedValue === 'number') {
		syncFromStore(storedValue);
	}

	const syncFromStore = (value: number) => {
		if (loading) {
			return;
		}
		if (lastSynced === value) {
			return;
		}
		lastSynced = value;
		idleTime = value;
		cacheIdle = value;
	};

	$: errorMaxtimeClose =
		idleTime !== '' && (+idleTime < min || +idleTime > max)
			? $format('generic_out_of_range', {
					default: `Value out of range`,
				})
			: '';

	const validate = () => {
		if (+idleTime > max) {
			idleTime = max;
		}
		if (+idleTime < min) {
			idleTime = min;
		}
		return idleTime;
	};

	const setIdlePowerPeriod = async () => {
		if (cacheIdle === idleTime) return;
		validate();
		loading = true;

		const res = await zwaveCall(device, {
			cc: 'Configuration',
			m: 'set',
			ep: 0,
			params: [{ parameter: setting, value: +idleTime }],
		});

		if (res.isok) {
			const resGet = await zwaveCall(device, {
				cc: 'Configuration',
				m: 'get',
				ep: 0,
				params: [+setting],
			});
			const fetched = parseConfigurationGetValue(resGet);
			const confirmed = typeof fetched === 'number' ? fetched : +idleTime;
			mergeWaveConfigurationParam(id, setting, confirmed);
			idleTime = confirmed;
			cacheIdle = confirmed;
			lastSynced = confirmed;
		}
		await onSettingsResponse(res, device);
		loading = false;
	};

	onMount(async () => {
		if (typeof storedValue === 'number') {
			return;
		}
		fetching = true;
		await fetchWaveConfigurationParam(device, paramKey);
		fetching = false;
	});
</script>

<Text styleType="body-small" class="mb-s"
	>{$format('idle_power_description', {
		values: { value: idleTime, min: min, max: max },
		default:
			'Define the maximum time before the power consumption of the motor is read from the Device, after one of the relays is switched on. If there is no power consumption during the set time (motor is not connected, damaged or requires longer time to start, motor is at the end position), the relay will switch off. This time is defined by entering it manually.',
	})}
</Text>
{#if fetching && idleTime === ''}
	<Text styleType="body-small" class="mb-s">
		{$format('loading', { default: 'Loading…' })}
	</Text>
{/if}
<Input
	class="mb-s"
	size="m"
	bind:value={idleTime}
	type={'number'}
	{min}
	{max}
	error={errorMaxtimeClose}
	info={$format('idle_power_values_description', {
		default: '3 - 50 = 0.3seconds – 5seconds (100ms resolution)',
	})}
	step={1}
	on:enter={() => {
		idleTime = validate();
	}}
	on:enter={setIdlePowerPeriod}
	id="input-zboutao"
	aria-label={$format('idle_power_description', {
		values: { value: idleTime, min: min, max: max },
		default:
			'Define the maximum time before the power consumption of the motor is read from the Device, after one of the relays is switched on. If there is no power consumption during the set time (motor is not connected, damaged or requires longer time to start, motor is at the end position), the relay will switch off. This time is defined by entering it manually.',
	})}
/>
<Button class="mt-s" {loading} size="m" width="full" on:click={setIdlePowerPeriod}
	>{$format('button_save')}</Button
>
